import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LANGUAGES } from '../../shared/languages';
import type { Allergen, DietaryConcernReason, DietaryTag } from '../../shared/menu';
import { useSettings, type OnboardingChoice } from '../lib/store';
import { defaultsFor, IDENTITY_CARDS } from '../lib/identity';
import { Logo } from '../components/Logo';
import { useT } from '../lib/i18n';

type Stage =
  | 'language'
  | 'identity'
  | 'allergens'
  | 'celiac-confirm'
  | 'halal-confirm'
  | 'kosher-confirm'
  | 'non-medical-grid';

const ALLERGEN_LABELS: Record<Allergen, string> = {
  nuts: 'Tree nuts',
  peanut: 'Peanuts',
  shellfish: 'Shellfish',
  fish: 'Fish',
  egg: 'Egg',
  soy: 'Soy',
  wheat: 'Wheat / Gluten',
  sesame: 'Sesame',
  dairy: 'Dairy',
  pork: 'Pork',
  alcohol: 'Alcohol',
};

const ALL_ALLERGENS: Allergen[] = ['nuts', 'peanut', 'shellfish', 'fish', 'egg', 'soy', 'wheat', 'sesame', 'dairy', 'pork', 'alcohol'];

const NON_MEDICAL_DIETARY: DietaryTag[] = ['vegan', 'vegetarian', 'pescatarian', 'gluten_free', 'dairy_free'];

const NON_MEDICAL_DIETARY_LABELS: Record<DietaryTag, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  pescatarian: 'Pescatarian',
  halal: 'Halal',
  kosher: 'Kosher',
  gluten_free: 'Gluten-free',
  dairy_free: 'Dairy-free',
};

export default function SplashScreen() {
  const navigate = useNavigate();
  const setLang = useSettings((s) => s.setTargetLanguage);
  const setHardFilterAllergens = useSettings((s) => s.setHardFilterAllergens);
  const setDietaryFlags = useSettings((s) => s.setDietaryFlags);
  const setDietaryReason = useSettings((s) => s.setDietaryReason);
  const setOnboardingChoice = useSettings((s) => s.setOnboardingChoice);
  const completeOnboarding = useSettings((s) => s.completeOnboarding);
  const t = useT();

  const [stage, setStage] = useState<Stage>('language');
  const [picked, setPicked] = useState<Set<Allergen>>(new Set());
  const [pickedDietary, setPickedDietary] = useState<Set<DietaryTag>>(new Set());
  const [reason, setReason] = useState<DietaryConcernReason>('ethical');

  const finish = (
    choice: OnboardingChoice,
    {
      allergens = [],
      dietary = [],
      reasonValue = null,
    }: { allergens?: Allergen[]; dietary?: DietaryTag[]; reasonValue?: DietaryConcernReason | null },
  ) => {
    setOnboardingChoice(choice);
    setHardFilterAllergens(allergens);
    setDietaryFlags(dietary);
    if (reasonValue) setDietaryReason(reasonValue);
    completeOnboarding();
    navigate('/', { replace: true });
  };

  const pickIdentity = (choice: OnboardingChoice) => {
    setOnboardingChoice(choice);
    if (choice === 'medical') setStage('allergens');
    else if (choice === 'celiac') setStage('celiac-confirm');
    else if (choice === 'halal') setStage('halal-confirm');
    else if (choice === 'kosher') setStage('kosher-confirm');
    else if (choice === 'non_medical') setStage('non-medical-grid');
    else finish('none', {});
  };

  const toggleAllergen = (a: Allergen) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  };

  const toggleDietary = (d: DietaryTag) => {
    setPickedDietary((cur) => {
      const next = new Set(cur);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  };

  // ---------- Stage: language ----------
  if (stage === 'language') {
    return (
      <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
        <div className="flex justify-center pt-8 pb-4">
          <Logo size="lg" />
        </div>
        <p style={{ color: 'var(--color-text-secondary)' }} className="text-center text-sm pb-8">
          {t('app.tagline')}
        </p>

        <div className="flex items-baseline justify-between pb-1 gap-3">
          <h1 className="text-2xl font-semibold" style={{ letterSpacing: '-0.01em' }}>
            {t('splash.question')}
          </h1>
          <InlineSkip onSkip={() => finish('none', {})} />
        </div>
        <p style={{ color: 'var(--color-text-secondary)' }} className="text-sm pb-6">
          Translations show in this language. Change it later in Settings.
        </p>

        <ul className="flex-1 flex flex-col gap-1 pb-4 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <li key={lang.code}>
              <button
                onClick={() => {
                  setLang(lang.code);
                  setStage('identity');
                }}
                className="w-full flex items-baseline justify-between text-left py-3 px-3 rounded-md"
                style={{
                  background: 'transparent',
                  border: 'none',
                  minHeight: 56,
                  borderBottom: '1px solid var(--color-divider)',
                }}
              >
                <span className="text-lg" style={{ fontWeight: 500 }}>{lang.nativeName}</span>
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{lang.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // ---------- Stage: identity (5-card picker) ----------
  if (stage === 'identity') {
    return (
      <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
        <div className="flex justify-center pt-8 pb-4">
          <Logo size="lg" />
        </div>

        <h1 className="text-2xl font-semibold pb-1" style={{ letterSpacing: '-0.01em' }}>
          What do we need to know?
        </h1>
        <p style={{ color: 'var(--color-text-secondary)' }} className="text-sm pb-2">
          The answer shapes how the kitchen reads your order. Change it later in Settings.
        </p>
        <p className="text-sm pb-5">
          <Link to="/safety" style={{ color: 'var(--color-danger)', textDecoration: 'underline', fontWeight: 600 }}>
            Read the safety guide first →
          </Link>
        </p>

        <div className="flex items-center justify-end pb-2" style={{ marginTop: -8 }}>
          <InlineSkip onSkip={() => finish('none', {})} />
        </div>

        <ul className="flex-1 flex flex-col gap-2 pb-4 overflow-y-auto">
          {IDENTITY_CARDS.map((c) => (
            <li key={c.choice}>
              <button
                onClick={() => pickIdentity(c.choice)}
                className="w-full text-left"
                style={{
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid var(--color-divider)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-primary)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 64,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600 }}>{c.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 400 }}>{c.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // ---------- Stage: allergens (medical) ----------
  if (stage === 'allergens') {
    return (
      <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
        <Header onBack={() => setStage('identity')} title="Which allergens?" />
        <p style={{ color: 'var(--color-text-secondary)' }} className="text-sm pb-2">
          Dishes with these get hidden, and a red allergy banner appears on the hand-off screen for the waiter.
        </p>
        <p className="text-sm pb-5">
          <Link to="/safety" style={{ color: 'var(--color-danger)', textDecoration: 'underline', fontWeight: 600 }}>
            Read the safety guide →
          </Link>
        </p>

        <div className="flex flex-wrap gap-2 pb-3 content-start">
          {ALL_ALLERGENS.map((a) => {
            const active = picked.has(a);
            return (
              <button
                key={a}
                onClick={() => toggleAllergen(a)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 9999,
                  border: `1px solid ${active ? 'var(--color-danger)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-danger)' : 'transparent',
                  color: active ? '#fff' : 'var(--color-primary)',
                  fontSize: 14,
                  minHeight: 44,
                }}
              >
                {ALLERGEN_LABELS[a]}
              </button>
            );
          })}
        </div>
        {picked.has('wheat') && (
          <div
            role="note"
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(196,51,43,0.06)',
              border: '1px solid rgba(196,51,43,0.25)',
              fontSize: 13,
              color: 'var(--color-primary)',
              lineHeight: 1.45,
              marginBottom: 16,
            }}
          >
            <strong>Celiac?</strong> Pick "I have celiac disease" instead — we'll auto-set cross-contamination protections.
          </div>
        )}
        <div className="flex-1" />
        <button
          onClick={() => finish('medical', { allergens: Array.from(picked), reasonValue: 'medical' })}
          className="btn-primary w-full"
          style={{ marginTop: 16 }}
        >
          {picked.size > 0 ? `Done · ${picked.size} selected` : 'Done'}
        </button>
      </div>
    );
  }

  // ---------- Stage: celiac confirm ----------
  if (stage === 'celiac-confirm') {
    return (
      <ConfirmStage
        title="Celiac disease"
        body={
          <>
            <p>
              <strong>Wheat / Gluten</strong> is on as a hard filter — wheat-flagged dishes get hidden from the menu.
            </p>
            <p>
              When you tag a dish strict, your hand-off card uses celiac-specific phrasing in the source language and asks the kitchen to confirm cross-contamination prep.
            </p>
            <p>
              Add <em>No cross-contamination</em> per dish for the strongest protection.
            </p>
          </>
        }
        onBack={() => setStage('identity')}
        onContinue={() => {
          const d = defaultsFor('celiac');
          finish('celiac', { allergens: d.allergens, dietary: d.dietary, reasonValue: d.reason });
        }}
      />
    );
  }

  // ---------- Stage: halal confirm ----------
  if (stage === 'halal-confirm') {
    return (
      <ConfirmStage
        title="Halal"
        body={
          <>
            <p>
              <strong>Pork</strong> and <strong>Alcohol</strong> are on as hard filters — flagged dishes get hidden from the menu.
            </p>
            <p>
              When you tag a dish strict, your hand-off card uses religious framing in the source language. Pork hidden as bacon, lard, gelatin, or stock is named explicitly.
            </p>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              Halal-certified meat confirmation lands soon. Until then, ask the waiter directly.
            </p>
          </>
        }
        onBack={() => setStage('identity')}
        onContinue={() => {
          const d = defaultsFor('halal');
          finish('halal', { allergens: d.allergens, dietary: d.dietary, reasonValue: d.reason });
        }}
      />
    );
  }

  // ---------- Stage: kosher confirm ----------
  if (stage === 'kosher-confirm') {
    return (
      <ConfirmStage
        title="Kosher"
        body={
          <>
            <p>
              <strong>Pork</strong> and <strong>Shellfish</strong> are on as hard filters — flagged dishes get hidden from the menu.
            </p>
            <p>
              When you tag a dish strict, your hand-off card uses religious framing in the source language. Hidden pork and shellfish (lard, gelatin, fish stock) are named explicitly.
            </p>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              Meat-and-dairy separation, kosher-meat confirmation, and a "fish with scales and fins only" filter land soon.
            </p>
          </>
        }
        onBack={() => setStage('identity')}
        onContinue={() => {
          const d = defaultsFor('kosher');
          finish('kosher', { allergens: d.allergens, dietary: d.dietary, reasonValue: d.reason });
        }}
      />
    );
  }

  // ---------- Stage: non-medical grid ----------
  if (stage === 'non-medical-grid') {
    return (
      <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
        <Header onBack={() => setStage('identity')} title="What do you avoid?" />
        <p style={{ color: 'var(--color-text-secondary)' }} className="text-sm pb-5">
          Pick one or more. Reason shapes how the kitchen reads your request.
        </p>

        <div className="flex flex-wrap gap-2 pb-6">
          {NON_MEDICAL_DIETARY.map((d) => {
            const active = pickedDietary.has(d);
            return (
              <button
                key={d}
                onClick={() => toggleDietary(d)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 9999,
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-bg)' : 'var(--color-primary)',
                  fontSize: 14,
                  minHeight: 44,
                }}
              >
                {NON_MEDICAL_DIETARY_LABELS[d]}
              </button>
            );
          })}
        </div>

        <h2 className="text-sm font-semibold pb-3" style={{ color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Reason
        </h2>
        <div className="flex flex-col gap-2 pb-6">
          {(['ethical', 'medical', 'celiac'] as DietaryConcernReason[]).map((r) => {
            const active = reason === r;
            const labels: Record<DietaryConcernReason, { label: string; hint: string }> = {
              ethical: { label: 'Ethical or preference', hint: 'For animals, environment, or just a personal choice' },
              medical: { label: 'Medical', hint: 'Doctor-recommended diet, but not a severe allergy' },
              celiac: { label: 'Celiac', hint: 'Pick "I have celiac disease" on the previous step instead' },
              halal: { label: '', hint: '' },
              kosher: { label: '', hint: '' },
            };
            const { label, hint } = labels[r];
            return (
              <button
                key={r}
                onClick={() => setReason(r)}
                className="text-left"
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-surface)' : 'transparent',
                  color: 'var(--color-primary)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 56,
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>{hint}</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        <button
          onClick={() => finish('non_medical', { dietary: Array.from(pickedDietary), reasonValue: reason })}
          className="btn-primary w-full"
          style={{ marginTop: 16 }}
        >
          {pickedDietary.size > 0 ? `Done · ${pickedDietary.size} selected` : 'Done'}
        </button>
      </div>
    );
  }

  return null;
}

// Low-emphasis escape hatch for curious one-tap visitors (e.g. someone
// clicking a LinkedIn post who wants to see what the app does before
// answering dietary questions). Keeps the default browser language and
// onboardingChoice 'none'; the user can set preferences later in Settings.
// Inline variant so it sits next to the stage heading and is visible
// without scrolling past the language list.
function InlineSkip({ onSkip }: { onSkip: () => void }) {
  return (
    <button
      type="button"
      onClick={onSkip}
      style={{
        background: 'transparent',
        border: 'none',
        color: 'var(--color-text-secondary)',
        fontSize: 13,
        fontWeight: 500,
        padding: '6px 4px',
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: 3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      Skip for now
    </button>
  );
}

function Header({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <header className="flex items-center gap-2 pt-4 pb-3">
      <button
        onClick={onBack}
        aria-label="Back"
        className="rounded-full inline-flex items-center justify-center"
        style={{ minWidth: 44, minHeight: 44, color: 'var(--color-primary)', background: 'transparent', border: 'none' }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 className="text-2xl font-semibold" style={{ letterSpacing: '-0.01em' }}>
        {title}
      </h1>
    </header>
  );
}

function ConfirmStage({
  title,
  body,
  onBack,
  onContinue,
}: {
  title: string;
  body: React.ReactNode;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
      <Header onBack={onBack} title={title} />
      <div
        style={{
          color: 'var(--color-primary)',
          fontSize: 14,
          lineHeight: 1.6,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          paddingBottom: 12,
        }}
      >
        {body}
      </div>
      <div style={{ paddingBottom: 12 }}>
        <Link to="/safety" style={{ color: 'var(--color-danger)', textDecoration: 'underline', fontWeight: 600, fontSize: 14 }}>
          Read the safety guide →
        </Link>
      </div>
      <div className="flex-1" />
      <button onClick={onContinue} className="btn-primary w-full" style={{ marginTop: 16 }}>
        Continue
      </button>
    </div>
  );
}
